import React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { PrayerDetails } from './PrayerDetails';
import { getTranslation } from '../utils/translations';
import { getTextColor } from '../utils/themeUtils';
import type { Prayer, Language, Theme } from '../types';

interface PrayerListProps {
  prayers: Prayer[];
  isDark: boolean;
  onTogglePrayer: (index: number) => void;
  onUpdateLocation: (index: number, location: string) => void;
  onUpdateActualTime: (index: number, time: string) => void;
  onUpdatePrayer: (index: number, updates: Partial<Prayer>) => void;
  language: Language;
  selectedDate: Date;
  isToday: boolean;
  theme: Theme;
  hasUnsavedChanges: boolean;
  isSyncing: boolean;
  onSave: () => void;
  saveSuccess?: boolean;
  saveError?: string | null;
}

export function PrayerList({ 
  prayers, 
  isDark, 
  onTogglePrayer, 
  onUpdateLocation, 
  onUpdateActualTime,
  onUpdatePrayer,
  language,
  selectedDate,
  isToday,
  theme,
  hasUnsavedChanges,
  isSyncing,
  onSave,
  saveSuccess,
  saveError
}: PrayerListProps) {
  const getPrayerName = (prayerName: string) => {
    const prayerKey = `prayer.${prayerName.toLowerCase()}`;
    return getTranslation(prayerKey, language);
  };

  const formatVerseRange = (prayer: Prayer, rakaat: 'first' | 'second') => {
    const startField = rakaat === 'first' ? 'surahFirstVerseStart' : 'surahSecondVerseStart';
    const endField = rakaat === 'first' ? 'surahFirstVerseEnd' : 'surahSecondVerseEnd';
    const start = prayer[startField];
    const end = prayer[endField];
    
    if (start && end) {
      return start === end ? `:${start}` : `:${start}-${end}`;
    }
    return '';
  };

  const formatDateForTitle = (date: Date) => {
    return date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      month: 'long',
      day: 'numeric'
    });
  };

  const getTimeStatusColor = (status: string) => {
    switch (status) {
      case 'ontime':
        return 'bg-green-100 text-green-800';
      case 'late':
        return 'bg-yellow-100 text-yellow-800';
      case 'qodo':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTimeStatusLabel = (status: string) => {
    switch (status) {
      case 'ontime':
        return 'On Time';
      case 'late':
        return 'Late';
      case 'qodo':
        return 'Qodo';
      default:
        return status;
    }
  };

  const getSurahMemoryLabel = (memory: string | undefined) => {
    switch (memory) {
      case 'remember':
        return 'Ingat';
      case 'forgot':
        return 'Lupa';
      case 'not-read':
        return 'Tidak Baca';
      default:
        return '';
    }
  };

  const getTravelStatusLabel = (prayer: Prayer) => {
    if (prayer.travelStatus === 'safar') {
      const methods = [];
      if (prayer.jamaType === 'jama') methods.push('Jama\'');
      if (prayer.qasharType === 'qashar') methods.push('Qashar');
      
      let label = 'Traveling';
      if (methods.length > 0) {
        label += ` (${methods.join(' + ')})`;
      }
      
      if (prayer.jamaDirection) {
        label += ` - ${prayer.jamaDirection === 'taqdim' ? 'Taqdim' : 'Taakhir'}`;
      }
      
      return label;
    }
    return null;
  };

  return (
    <div className={`rounded-xl shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'} p-4 sm:p-6 mb-6 sm:mb-8`}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
        <h2 className="text-lg sm:text-xl font-semibold">
          {isToday 
            ? getTranslation('prayers.todayTitle', language)
            : `${getTranslation('prayers.prayersFor', language)} ${formatDateForTitle(selectedDate)}`
          }
        </h2>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {prayers.map((prayer, index) => (
          <div
            key={prayer.name}
            className={`flex flex-col p-3 sm:p-4 rounded-lg ${
              isDark ? 'bg-gray-700' : 'bg-gray-50'
            } hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                <span className="text-base sm:text-lg font-medium truncate">
                  {getPrayerName(prayer.name)}
                </span>
                <div className="flex flex-col text-xs sm:text-sm">
                  <span className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {getTranslation('ui.scheduled', language)}: {prayer.scheduledTime}
                  </span>
                  {prayer.actualTime && (
                    <span style={getTextColor(theme)}>
                      {getTranslation('ui.actual', language)}: {prayer.actualTime}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => onTogglePrayer(index)}
                  className={`p-2 rounded-full transition-colors ${
                    prayer.completed
                      ? 'text-green-500 hover:text-green-600'
                      : 'text-gray-400 hover:text-gray-500'
                  }`}
                >
                  {prayer.completed ? (
                    <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6" />
                  ) : (
                    <XCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                  )}
                </button>
              </div>
            </div>
            
            {/* Prayer status indicators */}
            {prayer.completed && (
              <div className="flex flex-wrap gap-1 sm:gap-2 mt-2">
                {prayer.prayerStatus === 'jamaah' && (
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                    {getTranslation('prayer.status.jamaah', language)} {prayer.jamaahRole && `(${prayer.jamaahRole})`}
                  </span>
                )}
                {getTravelStatusLabel(prayer) && (
                  <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                    {getTravelStatusLabel(prayer)}
                  </span>
                )}
                {prayer.healthStatus === 'sakit' && (
                  <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                    Sick {prayer.sickPosition && `(${prayer.sickPosition === 'duduk' ? 'sitting' : 'lying down'})`}
                  </span>
                )}
                {prayer.timeStatus !== 'ontime' && (
                  <span className={`px-2 py-1 text-xs rounded-full ${getTimeStatusColor(prayer.timeStatus)}`}>
                    {getTimeStatusLabel(prayer.timeStatus)}
                  </span>
                )}
                {/* Show Surah Memory Status */}
                {prayer.surahFirstMemory && prayer.surahFirstMemory !== 'remember' && (
                  <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                    R1: {getSurahMemoryLabel(prayer.surahFirstMemory)}
                  </span>
                )}
                {prayer.surahSecondMemory && prayer.surahSecondMemory !== 'remember' && (
                  <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                    R2: {getSurahMemoryLabel(prayer.surahSecondMemory)}
                  </span>
                )}
                {/* Show Surah Details only if remembered */}
                {prayer.surahFirst && prayer.surahFirstMemory === 'remember' && (
                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    R1: {prayer.surahFirst.split('.')[1]?.trim()}{formatVerseRange(prayer, 'first')}
                  </span>
                )}
                {prayer.surahSecond && prayer.surahSecondMemory === 'remember' && (
                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    R2: {prayer.surahSecond.split('.')[1]?.trim()}{formatVerseRange(prayer, 'second')}
                  </span>
                )}
                {prayer.location && (
                  <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                    📍 {prayer.location.length > 20 ? prayer.location.substring(0, 20) + '...' : prayer.location}
                  </span>
                )}
              </div>
            )}

            <PrayerDetails
              prayer={prayer}
              index={index}
              isDark={isDark}
              onUpdatePrayer={onUpdatePrayer}
              onUpdateLocation={onUpdateLocation}
              onUpdateActualTime={onUpdateActualTime}
              language={language}
              theme={theme}
              hasUnsavedChanges={hasUnsavedChanges}
              isSyncing={isSyncing}
              onSave={onSave}
              saveSuccess={saveSuccess}
            />
          </div>
        ))}
      </div>
    </div>
  );
}